import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignedIn, UserButton, useAuth } from "@clerk/clerk-react";
import { authEnabled } from "../AuthGate";
import { adminWhoami } from "../../sim/admin";
import { Logo } from "../Logo";
import { CoinIcon } from "../Icon";
import { PetalsCoin } from "./PetalsCoin";
import { useGardenSummary, useWho } from "./visitor";
import { ClubNavPill } from "./GardenClub";
import { getAppConfig } from "../../sim/env";
import type { KickstarterConfig } from "../../sim/kickstarter";
import { track } from "../../sim/analytics";

/**
 * THE DEMO BANNER — top of every site page while Kickstarter mode is on. The site otherwise read as the finished
 * game (sprinklers, grow lights, a shop) to the visitors the campaign sends here, and never once linked back to
 * the campaign. One line: it's a free demo, where it stops, and the way to back it.
 */
function DemoBanner() {
  const [ks, setKs] = useState<{ cfg: KickstarterConfig; url: string } | null>(null);
  useEffect(() => {
    let alive = true;
    void getAppConfig().then((c) => {
      if (alive && c.kickstarter.on) setKs({ cfg: c.kickstarter, url: c.kickstarter.campaignUrl || c.prelaunch.kickstarterUrl });
    });
    return () => { alive = false; };
  }, []);
  if (!ks) return null;
  return (
    <div className="site-demo-banner" role="note" data-testid="site-demo-banner">
      <span><b>Free Kickstarter demo.</b> Play up to level {ks.cfg.levelCap}. The rest of the game opens at full release.</span>
      {ks.url && <a href={ks.url} target="_blank" rel="noopener noreferrer" onClick={() => track("cta_click", { id: "banner.kickstarter" })}>Back Shelf Garden on Kickstarter →</a>}
    </div>
  );
}

/**
 * THE WALLET — the player's coins and petals, large, top right of every site page. Read from their garden (this
 * device's save, and the cloud save when signed in — the same shared read the home page's welcome uses). Shown
 * only once there IS a garden: a first-time visitor has no purse to show, and a "0 / 0" would say nothing.
 */
function Wallet() {
  const who = useWho();
  const { garden } = useGardenSummary(who.signedIn);
  if (!garden) return null;
  return (
    <div className="site-wallet" aria-label="Your coins and Petals">
      <span className="site-wallet-chip coins" title="Coins"><CoinIcon size={22} /><b>{garden.coins.toLocaleString()}</b></span>
      <span className="site-wallet-chip petals" title="Petals"><PetalsCoin size={22} animate={false} /><b>{garden.petals.toLocaleString()}</b></span>
    </div>
  );
}

/** Admin-only nav bits: the 🛡️ Admin link AND an environment switcher that shows which server (LIVE or
 *  TEST) you're on and links to the OTHER deployment's SITE (its home page — the website version, not the
 *  game). Both render only for allowlisted admins (server-checked via /api/admin/me). The switcher only
 *  appears if the peer URL is configured (STAGING_URL on live, LIVE_URL on test). */
function AdminNavLink() {
  const { isSignedIn, getToken } = useAuth();
  const [me, setMe] = useState<{ admin: boolean; env: "production" | "staging"; stagingUrl: string | null; liveUrl: string | null } | null>(null);
  useEffect(() => {
    let alive = true;
    if (!isSignedIn) { setMe(null); return; }
    // ensure a token is available before asking, then check admin status + env
    void getToken();
    adminWhoami().then((r) => { if (alive) setMe({ admin: r.admin, env: r.env, stagingUrl: r.stagingUrl, liveUrl: r.liveUrl }); });
    return () => { alive = false; };
  }, [isSignedIn, getToken]);

  if (!me?.admin) return null;
  const onStaging = me.env === "staging";
  const peerUrl = onStaging ? me.liveUrl : me.stagingUrl;   // the OTHER server's base URL
  return (
    <>
      <Link to="/admin">🛡️ Admin</Link>
      {/* two explicit server buttons — the CURRENT server is highlighted (non-clickable), the OTHER
          links across to its site. Only rendered when the peer URL is configured. */}
      {peerUrl && (
        <span className="admin-env-toggle" role="group" aria-label="Server">
          {onStaging ? (
            <>
              <a className="env-tog" href={peerUrl}>🌐 Live</a>
              <span className="env-tog current test" aria-current="true">🧪 Test</span>
            </>
          ) : (
            <>
              <span className="env-tog current live" aria-current="true">🌐 Live</span>
              <a className="env-tog" href={peerUrl}>🧪 Test</a>
            </>
          )}
        </span>
      )}
    </>
  );
}

/**
 * Shared top navigation for the marketing site. Links to the site sections, and an
 * auth-aware action on the right: a "Sign in" button when signed out, the account
 * UserButton when signed in. In a no-auth build (no Clerk keys) it shows a plain
 * "Play" link instead, so the site still works without Clerk. Admins additionally
 * see an "Admin" link to the player-management backend.
 */
export function SiteNav() {
  const navigate = useNavigate();
  return (
    <>
    <DemoBanner />
    <nav className="site-nav">
      <Link to="/" className="site-brand" aria-label="Shelf Garden home"><Logo height={30} tone="light" /></Link>
      {/* No page links here any more: What's New / Shop / Profile are the big buttons on the home page, and the
          logo leads back to them. Only the admin's own tools remain (the Admin link + the Live/Test switch). */}
      <div className="site-nav-links">
        {authEnabled && <SignedIn><AdminNavLink /></SignedIn>}
      </div>
      {/* TOP RIGHT: the wallet, and (signed in) the small account menu — the only way to sign out. Play and
          Sign in live in the home page's hero now, where they are the biggest things on the page. */}
      <div className="site-nav-auth">
        <ClubNavPill />
        <Wallet />
        {authEnabled && (
          <SignedIn>
            <UserButton afterSignOutUrl="/">
              <UserButton.MenuItems>
                <UserButton.Action label="My Profile" labelIcon={<span>👤</span>} onClick={() => navigate("/profile")} />
                <UserButton.Action label="Garden Club" labelIcon={<span>🌸</span>} onClick={() => navigate("/garden-club")} />
                <UserButton.Action label="Redeem a code" labelIcon={<span>🎁</span>} onClick={() => navigate("/redeem")} />
              </UserButton.MenuItems>
            </UserButton>
          </SignedIn>
        )}
      </div>
    </nav>
    </>
  );
}
